<template>
    <Head title="Company Settings" />

    <div class="max-w-4xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Company Settings</h1>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage your company information and preferences
          </p>
        </div>
      </div>

      <form @submit.prevent="submit" class="space-y-6">
        <!-- Company Logo -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Company Logo
            </h3>
          </template>
          <div class="p-6">
            <div class="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
              <div class="w-24 h-24 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  v-if="logoPreview || company.logo_url"
                  :src="logoPreview || company.logo_url"
                  :alt="company.name"
                  class="w-full h-full object-cover"
                />
                <span v-else class="text-3xl font-bold text-white">
                  {{ company.name.charAt(0).toUpperCase() }}
                </span>
              </div>
              <div class="flex-1 space-y-3">
                <input
                  type="file"
                  ref="logoInput"
                  @change="handleLogoChange"
                  accept="image/jpeg,image/png,image/jpg"
                  class="hidden"
                />
                <div class="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    @click="$refs.logoInput.click()"
                    :disabled="uploadingLogo"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {{ uploadingLogo ? 'Uploading...' : (company.logo_url ? 'Change Logo' : 'Upload Logo') }}
                  </Button>
                  <Button
                    v-if="company.logo_url"
                    type="button"
                    variant="danger"
                    @click="confirmDeleteLogo"
                    :disabled="deletingLogo"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {{ deletingLogo ? 'Removing...' : 'Remove Logo' }}
                  </Button>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  Recommended: 400x400px, PNG or JPG. Maximum file size: 5MB.
                </p>
                <p v-if="logoError" class="text-sm text-red-600 dark:text-red-400">
                  {{ logoError }}
                </p>
              </div>
            </div>
          </div>
        </Card>

        <!-- Basic Information -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Basic Information
            </h3>
          </template>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                v-model="form.name"
                label="Company Name"
                :error="form.errors.name"
                required
                placeholder="Enter company name"
                class="md:col-span-2"
              />
              <Select
                v-model="form.industry_type"
                label="Industry"
                :options="industryOptions"
                :error="form.errors.industry_type"
                placeholder="Select industry"
              />
              <Select
                v-model="form.company_size"
                label="Company Size"
                :options="companySizeOptions"
                :error="form.errors.company_size"
                placeholder="Select company size"
              />
            </div>
          </div>
        </Card>

        <!-- Address -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Address
            </h3>
          </template>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                v-model="form.address_line_1"
                label="Address Line 1"
                :error="form.errors.address_line_1"
                required
                placeholder="Street address"
                class="md:col-span-2"
              />
              <Input
                v-model="form.address_line_2"
                label="Address Line 2"
                :error="form.errors.address_line_2"
                placeholder="Apartment, suite, unit, building, floor, etc."
                class="md:col-span-2"
              />
              <Input
                v-model="form.city"
                label="City"
                :error="form.errors.city"
                required
                placeholder="City"
              />
              <Input
                v-model="form.state_province"
                label="State / Province"
                :error="form.errors.state_province"
                required
                placeholder="State or province"
              />
              <Input
                v-model="form.postal_code"
                label="Postal Code"
                :error="form.errors.postal_code"
                required
                placeholder="ZIP or postal code"
              />
              <Select
                v-model="form.country_id"
                label="Country"
                :options="countryOptions"
                :error="form.errors.country_id"
                required
                searchable
                placeholder="Select country"
              />
            </div>
          </div>
        </Card>

        <!-- Contact Information -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Contact Information
            </h3>
          </template>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                v-model="form.phone"
                label="Phone Number"
                type="tel"
                :error="form.errors.phone"
                required
                placeholder="+1 (555) 123-4567"
              >
                <template #prefix>
                  <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </template>
              </Input>
              <Input
                v-model="form.website"
                label="Website"
                type="url"
                :error="form.errors.website"
                placeholder="https://www.example.com"
              >
                <template #prefix>
                  <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </template>
              </Input>
            </div>
          </div>
        </Card>

        <!-- Localization -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Localization
            </h3>
          </template>
          <div class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Select
                  v-model="form.currency_id"
                  label="Currency"
                  :options="currencyOptions"
                  :error="form.errors.currency_id"
                  required
                  disabled
                  placeholder="Select currency"
                />
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  <svg class="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                  </svg>
                  Currency is automatically set based on your country
                </p>
              </div>
              <Select
                v-model="form.timezone_id"
                label="Timezone"
                :options="timezoneOptions"
                searchable
                :error="form.errors.timezone_id"
                required
                placeholder="Select timezone"
              />
            </div>
            <div v-if="selectedCurrency" class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div class="flex items-start">
                <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <div>
                  <h4 class="text-sm font-medium text-blue-800 dark:text-blue-200">Currency Information</h4>
                  <p class="mt-1 text-sm text-blue-700 dark:text-blue-300">
                    Selected currency: <strong>{{ selectedCurrency.label }}</strong>
                  </p>
                  <p class="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    To change your currency, please update your country selection or contact support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <!-- External Request Portal -->
        <Card>
          <template #header>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              External Request Portal
            </h3>
          </template>
          <div class="p-6">
            <div class="space-y-4">
              <div>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Share this portal link with external users to allow them to submit incidents, work orders, and helpdesk tickets without requiring an account.
                </p>
                <div class="flex items-center gap-3">
                  <div class="flex-1">
                    <Input
                      :model-value="portalUrl"
                      label="Portal URL"
                      readonly
                      class="font-mono text-sm"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    @click="copyPortalUrl"
                    class="mt-6"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {{ copied ? 'Copied!' : 'Copy' }}
                  </Button>
                </div>
              </div>
              <div class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div class="flex items-center">
                  <div :class="[
                    'w-2 h-2 rounded-full mr-3',
                    company.portal_enabled ? 'bg-green-500' : 'bg-gray-400'
                  ]"></div>
                  <div>
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Portal Status: {{ company.portal_enabled ? 'Enabled' : 'Disabled' }}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      {{ company.portal_enabled ? 'External users can submit requests via this portal' : 'Portal is currently disabled' }}
                    </p>
                  </div>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="portalEnabled"
                    @change="togglePortal"
                    class="sr-only peer"
                  >
                  <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        </Card>

        <!-- Submit Buttons -->
        <div class="flex flex-col sm:flex-row justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            @click="resetForm"
            :disabled="form.processing"
            class="w-full sm:w-auto"
          >
            Reset Changes
          </Button>
          <Button
            type="submit"
            :disabled="form.processing"
            class="w-full sm:w-auto"
          >
            <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ form.processing ? 'Saving...' : 'Save Changes' }}
          </Button>
        </div>
      </form>

      <!-- Delete Logo Confirmation Modal -->
      <Modal :show="showDeleteLogoModal" @close="showDeleteLogoModal = false" max-width="sm">
        <template #header>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Remove Company Logo
          </h3>
        </template>
        <template #content>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to remove your company logo? This action cannot be undone.
          </p>
          <div class="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="secondary"
              @click="showDeleteLogoModal = false"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              @click="deleteLogo"
              :disabled="deletingLogo"
            >
              {{ deletingLogo ? 'Removing...' : 'Remove Logo' }}
            </Button>
          </div>
        </template>
      </Modal>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Head, useForm, router } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Input from '@/Components/Common/Input.vue';
import Select from '@/Components/Common/Select.vue';
import Modal from '@/Components/Common/Modal.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  countries: {
    type: Array,
    default: () => [],
  },
  currencies: {
    type: Array,
    default: () => [],
  },
  timezones: {
    type: Array,
    default: () => [],
  },
  industries: {
    type: Array,
    default: () => [
      { value: 'technology', label: 'Technology' },
      { value: 'healthcare', label: 'Healthcare' },
      { value: 'finance', label: 'Finance' },
      { value: 'education', label: 'Education' },
      { value: 'retail', label: 'Retail' },
      { value: 'manufacturing', label: 'Manufacturing' },
      { value: 'real_estate', label: 'Real Estate' },
      { value: 'hospitality', label: 'Hospitality' },
      { value: 'other', label: 'Other' },
    ],
  },
  companySizes: {
    type: Array,
    default: () => [
      { value: '1-10', label: '1-10 employees' },
      { value: '11-50', label: '11-50 employees' },
      { value: '51-200', label: '51-200 employees' },
      { value: '201-500', label: '201-500 employees' },
      { value: '501-1000', label: '501-1000 employees' },
      { value: '1000+', label: '1000+ employees' },
    ],
  },
});

const { success, error } = useToast();
const logoInput = ref(null);
const logoPreview = ref(null);
const logoError = ref('');
const uploadingLogo = ref(false);
const deletingLogo = ref(false);
const showDeleteLogoModal = ref(false);
const copied = ref(false);
const portalEnabled = ref(props.company.portal_enabled || false);

// Form setup
const form = useForm({
  name: props.company.name || '',
  industry_type: props.company.industry_type || '',
  company_size: props.company.company_size || '',
  address_line_1: props.company.address_line_1 || '',
  address_line_2: props.company.address_line_2 || '',
  city: props.company.city || '',
  state_province: props.company.state_province || '',
  postal_code: props.company.postal_code || '',
  country_id: props.company.country_id || null,
  currency_id: props.company.currency_id || null,
  timezone_id: props.company.timezone_id || null,
  phone: props.company.phone || '',
  website: props.company.website || '',
});

/**
 * Format country options for select dropdown
 */
const countryOptions = computed(() => {
  return props.countries.map(country => ({
    value: country.id,
    label: country.name,
  }));
});

/**
 * Format currency options for select dropdown
 */
const currencyOptions = computed(() => {
  return props.currencies.map(currency => ({
    value: currency.id,
    label: `${currency.code} (${currency.symbol}) - ${currency.name}`,
  }));
});

/**
 * Format timezone options for select dropdown
 */
const timezoneOptions = computed(() => {
  return props.timezones.map(tz => ({
    value: tz.id,
    label: tz.display_name || tz.name,
  }));
});

/**
 * Format industry options for select dropdown
 */
const industryOptions = computed(() => {
  return props.industries.map(industry => ({
    value: industry.value,
    label: industry.label,
  }));
});

/**
 * Format company size options for select dropdown
 */
const companySizeOptions = computed(() => {
  return props.companySizes.map(size => ({
    value: size.value,
    label: size.label,
  }));
});

/**
 * Get selected currency details
 */
const selectedCurrency = computed(() => {
  if (!form.currency_id) return null;
  const currency = props.currencies.find(c => c.id === form.currency_id);
  if (!currency) return null;
  return {
    value: currency.id,
    label: `${currency.code} (${currency.symbol}) - ${currency.name}`,
  };
});

/**
 * Generate portal URL
 */
const portalUrl = computed(() => {
  if (!props.company.public_portal_uuid) {
    return 'Portal URL not available';
  }
  return `${window.location.origin}/external-request/${props.company.public_portal_uuid}`;
});

/**
 * Watch for country changes and auto-update currency
 */
watch(() => form.country_id, (newCountryId) => {
  if (newCountryId) {
    updateCurrency(newCountryId);
  }
});

/**
 * Auto-update currency based on selected country
 */
const updateCurrency = (countryId) => {
  const country = props.countries.find(c => c.id === countryId);
  if (country && country.default_currency_id) {
    form.currency_id = country.default_currency_id;
  }
};

/**
 * Submit company settings form
 */
const submit = () => {
  form.put(route('settings.company.update'), {
    preserveScroll: true,
    onSuccess: () => {
      success('Company settings updated successfully');
    },
    onError: (errors) => {
      error('Failed to update company settings. Please check the form for errors.');
    },
  });
};

/**
 * Reset form to original values
 */
const resetForm = () => {
  form.reset();
  logoPreview.value = null;
  logoError.value = '';
};

/**
 * Handle logo file selection
 */
const handleLogoChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  if (!validTypes.includes(file.type)) {
    logoError.value = 'Please select a valid image file (JPG or PNG)';
    return;
  }

  // Validate file size (5MB max)
  const maxSize = 5 * 1024 * 1024; // 5MB in bytes
  if (file.size > maxSize) {
    logoError.value = 'File size must be less than 5MB';
    return;
  }

  logoError.value = '';
  uploadLogo(file);
};

/**
 * Upload logo to server
 */
const uploadLogo = (file) => {
  uploadingLogo.value = true;
  const formData = new FormData();
  formData.append('logo', file);

  // Create preview
  const reader = new FileReader();
  reader.onload = (e) => {
    logoPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);

  router.post(route('settings.company.logo'), formData, {
    preserveScroll: true,
    onSuccess: (page) => {
      uploadingLogo.value = false;
      logoPreview.value = null;
      success('Company logo uploaded successfully');
    },
    onError: (errors) => {
      uploadingLogo.value = false;
      logoPreview.value = null;
      logoError.value = errors.logo || 'Failed to upload logo';
      error('Failed to upload logo');
    },
    onFinish: () => {
      // Reset file input
      if (logoInput.value) {
        logoInput.value.value = '';
      }
    },
  });
};

/**
 * Confirm logo deletion
 */
const confirmDeleteLogo = () => {
  showDeleteLogoModal.value = true;
};

/**
 * Delete company logo
 */
const deleteLogo = () => {
  deletingLogo.value = true;
  router.delete(route('settings.company.logo.delete'), {
    preserveScroll: true,
    onSuccess: () => {
      deletingLogo.value = false;
      showDeleteLogoModal.value = false;
      logoPreview.value = null;
      success('Company logo removed successfully');
    },
    onError: () => {
      deletingLogo.value = false;
      error('Failed to remove logo');
    },
  });
};

/**
 * Copy portal URL to clipboard
 */
const copyPortalUrl = async () => {
  try {
    await navigator.clipboard.writeText(portalUrl.value);
    copied.value = true;
    success('Portal URL copied to clipboard');
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (err) {
    error('Failed to copy URL to clipboard');
  }
};

/**
 * Toggle portal enabled/disabled status
 */
const togglePortal = () => {
  router.put(route('settings.company.portal.toggle'), {
    portal_enabled: portalEnabled.value,
  }, {
    preserveScroll: true,
    onSuccess: () => {
      success(`Portal ${portalEnabled.value ? 'enabled' : 'disabled'} successfully`);
    },
    onError: () => {
      // Revert the toggle on error
      portalEnabled.value = !portalEnabled.value;
      error('Failed to update portal status');
    },
  });
};
</script>
